import { createContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import AuthServices from '../services/AuthServices';
import { setToken as setHttpToken } from '../services/httpServices';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    // Initialize userrole from localStorage if token exists (for consistency)
    const [userrole, setUserrole] = useState(
        localStorage.getItem('userrole') || ""
    );
    const [token, setToken] = useState(localStorage.getItem('token') || null);
    const [loading, setLoading] = useState(true); // Add loading state

    const decodeTokenAndSetUser = (token, preserveRole = false) => {
        // Guard against undefined/null tokens (e.g., when mustChangePassword is true)
        if (!token || typeof token !== 'string') {
            console.warn("decodeTokenAndSetUser called with invalid token:", token);
            setLoading(false);
            return;
        }
        try {
            const decodedToken = JSON.parse(atob(token.split('.')[1]));
            const currentTime = Date.now() / 1000;
            // Token decoded successfully
            if (decodedToken.exp < currentTime) {
                console.error("Token expired");
                logoutUser();
            } else {
                setHttpToken(token);
                setUser(decodedToken.id);
                // If preserveRole is true, don't overwrite the role (used during login flow)
                // Otherwise, use role from token (source of truth) and store as string for consistency
                if (!preserveRole) {
                    const tokenRole = decodedToken.role;
                    const roleString = String(tokenRole);
                    setUserrole(roleString);
                    // Update localStorage to match token (token is source of truth)
                    localStorage.setItem('userrole', roleString);
                } else {
                    // Just verify the token is valid, but don't change the role
                    console.log("decodeTokenAndSetUser: Preserving existing role during login");
                }
            }
        } catch (error) {
            console.error("Failed to decode token:", error);
            logoutUser();
        } finally {
            setLoading(false); // Set loading to false after decoding
        }
    };

    useEffect(() => {
        if (token) {
            setHttpToken(token);
            // Check if role was just set (within last 2 seconds) - if so, preserve it
            // This prevents race condition where login sets role but useEffect overwrites it
            const storedRole = localStorage.getItem('userrole');
            const roleTimestamp = localStorage.getItem('userrole_timestamp');
            const now = Date.now();
            const recentlySet = roleTimestamp && (now - parseInt(roleTimestamp, 10)) < 2000;

            if (recentlySet && storedRole) {
                // Role was just set during login, preserve it
                decodeTokenAndSetUser(token, true); // preserveRole = true
            } else {
                // Normal token decode - use role from token
                decodeTokenAndSetUser(token, false);
            }
        } else {
            setHttpToken(null);
            setLoading(false); // Set loading to false if no token exists
        }
    }, [token]);

    const loginUser = async (credentials) => {
        try {
            // Clear old session data first to prevent conflicts from multiple logins
            logoutUser();

            const data = await AuthServices.login(credentials);

            // Check if password must be changed (no token returned, only resetToken)
            if (data.mustChangePassword === true && data.resetToken) {
                console.log("Password change required - redirecting to reset password");

                // Store reset token and email for password reset
                localStorage.setItem('resetToken', data.resetToken);
                localStorage.setItem('resetEmail', data.email);

                // Determine user type for the reset URL
                const searchParams = new URLSearchParams(window.location.search);
                const userTypeFromUrl = searchParams.get('type') || 'teacher';

                // Redirect to reset password page
                const resetUrl = `/reset-password/${data.resetToken}?type=${userTypeFromUrl}&email=${encodeURIComponent(data.email || '')}`;
                console.log("Redirecting to:", resetUrl);
                window.location.href = resetUrl;

                return data;
            }

            // Normal login flow - token should exist
            if (!data.token) {
                throw new Error("No token received from server");
            }

            // Always store role as string for consistency - SET IMMEDIATELY BEFORE token
            // This ensures role is set before useEffect triggers
            const roleString = String(data.role);
            setUserrole(roleString);
            localStorage.setItem('userrole', roleString);
            // Store timestamp to prevent useEffect from overwriting during login flow
            localStorage.setItem('userrole_timestamp', Date.now().toString());

            // Store user data
            localStorage.setItem('user', JSON.stringify(data.user));

            // Set token AFTER role is set - this will trigger useEffect but role is already set
            setToken(data.token);
            localStorage.setItem('token', data.token);
            setHttpToken(data.token);

            // Decode token to set user ID, but preserve the role from login response
            // The login response role is the source of truth (handles "admin" string vs number)
            decodeTokenAndSetUser(data.token, true); // preserveRole = true

            // Final role is from login response (handles "admin" string properly)
            const finalRole = String(data.role);

            console.log("Login complete - role set to:", finalRole, "type:", typeof finalRole);
            console.log("Login complete - localStorage check:", {
                token: localStorage.getItem('token') ? 'SET' : 'MISSING',
                userrole: localStorage.getItem('userrole'),
                user: localStorage.getItem('user') ? 'SET' : 'MISSING',
                roleTimestamp: localStorage.getItem('userrole_timestamp')
            });

            // Decode token to verify role matches
            try {
                const decodedToken = JSON.parse(atob(data.token.split('.')[1]));
                console.log("Login complete - Token role:", decodedToken.role, "Response role:", data.role);
                if (String(decodedToken.role) !== String(data.role)) {
                    console.warn("⚠️ Role mismatch! Token has:", decodedToken.role, "but response has:", data.role);
                }
            } catch (e) {
                console.error("Error decoding token for verification:", e);
            }

            // CRITICAL FIX: If role is 4 (state admin), trigger navigation immediately
            // This works even if Login component code is cached
            const roleNum = Number(finalRole);
            const isStateAdmin = roleNum === 4 || finalRole === '4' || finalRole === 4;

            console.log("AuthContext: Role check for redirect", {
                finalRole,
                roleNum,
                isStateAdmin,
                currentPath: window.location.pathname
            });

            if (isStateAdmin) {
                console.log("AuthContext: State admin detected, FORCING navigation to dashboard");
                // Use window.location.href for guaranteed redirect (bypasses React Router)
                // Check path to avoid redirect loops
                setTimeout(() => {
                    const currentPath = window.location.pathname;
                    console.log("AuthContext: Redirect check", {
                        currentPath,
                        willRedirect: currentPath === '/login' || currentPath === '/login/'
                    });

                    if (currentPath === '/login' || currentPath === '/login/' || currentPath.includes('login')) {
                        console.log("AuthContext: EXECUTING redirect to /admin/state-dashboard");
                        window.location.href = "/admin/state-dashboard";
                    } else {
                        console.log("AuthContext: Not on login page, skipping redirect", { currentPath });
                    }
                }, 200);
            }

            return data;
        } catch (error) {
            console.error("Login failed:", error);
            throw error;
        }
    };

    const logoutUser = () => {
        setToken(null);
        setUser(null);
        setUserrole("");
        setHttpToken(null);
        localStorage.removeItem('token');
        localStorage.removeItem('userrole');
        localStorage.removeItem('isteacher');
        localStorage.removeItem('user');
        localStorage.removeItem('needsPayment');
        localStorage.removeItem('paymentDetails');
    };

    const autoLoginUser = (token, role, user) => {
        // Clear old session data first
        logoutUser();

        setToken(token);
        localStorage.setItem('token', token);
        setHttpToken(token);
        // Always store role as string for consistency
        const roleString = String(role);
        setUserrole(roleString);
        localStorage.setItem('userrole', roleString);
        localStorage.setItem('user', JSON.stringify(user));
        decodeTokenAndSetUser(token);
        return true;
    };

    const registerUser = async (userdetails) => {
        const data = await AuthServices.register(userdetails);
        if (!data || !data.token) {
            throw new Error("Registration failed: No token received");
        }
        await autoLoginUser(data.token, data.role, data.user);
        return data;
    };

    const registerTeacher = async (userdetails) => {
        const data = await AuthServices.teacherregister(userdetails);
        if (!data || !data.token) {
            throw new Error("Teacher registration failed: No token received");
        }
        await autoLoginUser(data.token, data.role, data.user);
        return data;
    };

    const registerSchool = async (userdetails) => {
        const data = await AuthServices.schoolregister(userdetails);
        if (!data || !data.token) {
            throw new Error("School registration failed: No token received");
        }
        await autoLoginUser(data.token, data.role, data.user);
        return data;
    };

    return (
        <AuthContext.Provider value={{
            user,
            token,
            userrole,
            loading, // Expose loading state
            loginUser,
            logoutUser,
            registerUser,
            registerTeacher,
            registerSchool

        }}>
            {children}
        </AuthContext.Provider>
    );
};

AuthProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export default AuthContext;